import React, { useState, useEffect } from "react";
import { Form, Select, Button, Spin, message, Tag } from "antd";
import { 
  fetchPartnerOrganizations, 
  fetchTalentProfiles 
} from "../../../../utils/dropdownUtils";

const { Option } = Select;

const TalentSelection = ({ initialData, onNext, isFirstStep, isEditMode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [partnerOrganizations, setPartnerOrganizations] = useState([]);
  const [talents, setTalents] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(initialData.partnerOrganization);
  const [selectedTalent, setSelectedTalent] = useState(initialData.talentProfile);
  const [talentInfo, setTalentInfo] = useState({
    partnerRate: initialData.talentBudget || 0,
    clientRate: initialData.clientRate || 0,
    settledCost: initialData.settledCost || 0,
    estimatedProfit: initialData.estimatedProfit || 0,
    location: initialData.talentLocation || "",
    primarySkills: initialData.primarySkills || [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedPartner && talents.length > 0) {
      const partnerTalents = talents.filter(t => t.partnerOrganization === selectedPartner);
      if (initialData.talentProfile) {
        const talent = partnerTalents.find(t => t.id === initialData.talentProfile);
        if (talent) {
          updateTalentInfo(talent);
        }
      }
    }
  }, [selectedPartner, talents, initialData.talentProfile]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [partners, talentsData] = await Promise.all([
        fetchPartnerOrganizations(),
        fetchTalentProfiles(),
      ]);

      setPartnerOrganizations(partners);
      setTalents(talentsData);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateTalentInfo = (talent) => {
    const partnerRate = talent.partnerRate || talent.rate || 0;
    const clientRate = talent.clientRate || 0;
    const settledCost = talent.settledCost || 0;
    const estimatedProfit = clientRate - settledCost;
    
    setTalentInfo({
      partnerRate,
      clientRate,
      settledCost,
      estimatedProfit,
      location: talent.location || "-",
      primarySkills: talent.primarySkills || [],
    });
  };

  const handlePartnerChange = (partnerName) => {
    setSelectedPartner(partnerName);
    setSelectedTalent("");
    setTalentInfo({ 
      partnerRate: 0, 
      clientRate: 0, 
      settledCost: 0, 
      estimatedProfit: 0, 
      location: "", 
      primarySkills: [] 
    });
    form.setFieldsValue({
      talentProfile: undefined,
    });
  };

  const handleTalentChange = (talentId) => {
    setSelectedTalent(talentId);
    const talent = talents.find(t => t.id === talentId);
    
    if (talent) {
      updateTalentInfo(talent);
    }
  };

  const handleSubmit = (values) => {
    const talent = talents.find(t => t.id === values.talentProfile);
    const partnerRate = talent?.partnerRate || talent?.rate || talentInfo.partnerRate || 0;
    
    onNext({
      partnerOrganization: values.partnerOrganization,
      talentId: values.talentProfile,
      talentProfile: values.talentProfile,
      talentEmail: talent?.email || "",
      talentBudget: partnerRate,
      talentLocation: talentInfo.location,
      monthlyRate: partnerRate,
      clientRate: talentInfo.clientRate,
      settledCost: talentInfo.settledCost,
      primarySkills: talentInfo.primarySkills,
    });
  };

  const availableTalents = selectedPartner 
    ? talents.filter(t => t.partnerOrganization === selectedPartner)
    : [];

  const formatCurrency = (value) => {
    return `₹ ${parseFloat(value || 0).toLocaleString("en-IN")}`;
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        partnerOrganization: initialData.partnerOrganization,
        talentProfile: initialData.talentProfile,
      }}
    >
      <div className="two-column-grid">
        <Form.Item
          name="partnerOrganization"
          label="Partner Organization"
          rules={[{ required: true, message: "Please select a partner organization" }]}
        >
          <Select
            placeholder="Select Partner Organization"
            onChange={handlePartnerChange}
            showSearch
            filterOption={(input, option) =>
              (option?.children || "").toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={isEditMode}
          >
            {partnerOrganizations.map((partner) => (
              <Option key={partner.id} value={partner.name}>
                {partner.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="talentProfile"
          label="Talent Profile (Email ID - Name)"
          rules={[{ required: true, message: "Please select a talent profile" }]}
        >
          <Select
            placeholder="Select Talent Profile"
            onChange={handleTalentChange}
            disabled={!selectedPartner || isEditMode}
            showSearch
            filterOption={(input, option) =>
              (option?.children || "").toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {availableTalents.map((talent) => (
              <Option key={talent.id} value={talent.id}>
                {talent.email} - {talent.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      {talentInfo.partnerRate > 0 && (
        <div className="info-display" style={{ marginTop: 16, padding: 16, background: "#fafafa", borderRadius: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            <div className="info-item">
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Partner Rate (Monthly):</span>
              <span className="value" style={{ marginLeft: 8, fontWeight: 600 }}>{formatCurrency(talentInfo.partnerRate)} / Month</span>
            </div>
            <div className="info-item">
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Client Rate:</span>
              <span className="value" style={{ marginLeft: 8, fontWeight: 600, color: "#1890ff" }}>{formatCurrency(talentInfo.clientRate)} / Month</span>
            </div>
            <div className="info-item">
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Settled Cost:</span>
              <span className="value" style={{ marginLeft: 8, fontWeight: 600, color: "#fa8c16" }}>{formatCurrency(talentInfo.settledCost)} / Month</span>
            </div>
            <div className="info-item">
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Estimated Profit:</span>
              <span className="value" style={{ marginLeft: 8, fontWeight: 600, color: talentInfo.estimatedProfit > 0 ? "#52c41a" : "#ff4d4f" }}>
                {formatCurrency(talentInfo.estimatedProfit)} / Month
              </span>
            </div>
            <div className="info-item">
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Location:</span>
              <span className="value" style={{ marginLeft: 8 }}>{talentInfo.location || "-"}</span>
            </div>
            <div className="info-item" style={{ gridColumn: "1 / -1" }}>
              <span className="label" style={{ fontWeight: 500, color: "#666" }}>Primary Skills:</span>
              <div style={{ marginTop: 8 }}>
                {talentInfo.primarySkills && talentInfo.primarySkills.length > 0 ? (
                  talentInfo.primarySkills.map((skill, index) => (
                    <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                      {skill}
                    </Tag>
                  ))
                ) : (
                  <span style={{ color: "#999" }}>No skills specified</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="form-actions" style={{ marginTop: 24 }}>
        <Button type="primary" htmlType="submit">
          Save & Next
        </Button>
      </div>
    </Form>
  );
};

export default TalentSelection;
